import React, { useRef } from "react";
import styled from "styled-components";

const List = styled.div`
  display: flex;
  gap: 4px;
  overflow-x: auto;
  overflow-y: hidden;
  border-bottom: 1px solid ${(p) => p.theme.colors.border};
  margin-bottom: 24px;
  scrollbar-width: thin;
  @media (max-width: 600px) {
    margin-bottom: 18px;
  }
`;
const Tab = styled.button<{ $active: boolean }>`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 10px 16px;
  margin-bottom: -1px;
  border-bottom: 2px solid
    ${(p) => (p.$active ? p.theme.colors.primary : "transparent")};
  color: ${(p) =>
    p.$active ? p.theme.colors.text : p.theme.colors.textSecondary};
  font-weight: ${(p) => (p.$active ? 650 : 500)};
  font-size: 15px;
  transition:
    color ${(p) => p.theme.motion.fast},
    border-color ${(p) => p.theme.motion.fast};
  &:hover {
    color: ${(p) => p.theme.colors.text};
  }
`;

export interface TabItem<K extends string> {
  key: K;
  label: React.ReactNode;
  badge?: React.ReactNode;
}

export default function Tabs<K extends string>({
  items,
  value,
  onChange,
  label,
}: {
  items: TabItem<K>[];
  value: K;
  onChange: (key: K) => void;
  label: string;
}) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  function onKey(e: React.KeyboardEvent, i: number) {
    let next = -1;
    if (e.key === "ArrowRight") next = (i + 1) % items.length;
    if (e.key === "ArrowLeft") next = (i - 1 + items.length) % items.length;
    if (e.key === "Home") next = 0;
    if (e.key === "End") next = items.length - 1;
    if (next < 0) return;
    e.preventDefault();
    onChange(items[next].key);
    refs.current[next]?.focus();
  }
  return (
    <List role="tablist" aria-label={label}>
      {items.map((it, i) => (
        <Tab
          key={it.key}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="button"
          role="tab"
          aria-selected={value === it.key}
          tabIndex={value === it.key ? 0 : -1}
          $active={value === it.key}
          onClick={() => onChange(it.key)}
          onKeyDown={(e) => onKey(e, i)}
        >
          {it.label}
          {it.badge}
        </Tab>
      ))}
    </List>
  );
}
